import { urlIsGame } from '@fanduel/rewards-hub-client/promotion-utils';
import { buildLauncherBaseUrl, type LauncherUrlResolutionContext } from '@/utils/url/launcher-url';
import type { GameLaunchMode } from '@/utils/url/game-launch-mode';
import { ddRumError } from '@/utils/datadog';

const QUICKLAUNCH_PARAM = 'quicklaunch';
const LAUNCHER_HOSTNAME_PATTERN = /(?:^|\.)launcher\./;

export const isQuicklaunchLauncherUrl = (url: string): boolean => {
    try {
        const parsedUrl = new URL(url);
        const quicklaunch = parsedUrl.searchParams.get(QUICKLAUNCH_PARAM);
        if (!quicklaunch) return false;
        return urlIsGame(quicklaunch) || LAUNCHER_HOSTNAME_PATTERN.test(new URL(quicklaunch).hostname);
    } catch {
        return false;
    }
};

export const resolveClgUrl = (
    url: string,
    resolution: LauncherUrlResolutionContext,
    launcherMode?: GameLaunchMode
): string => {
    try {
        const quicklaunch = new URL(url).searchParams.get(QUICKLAUNCH_PARAM);
        if (quicklaunch) {
            return resolveClgUrl(quicklaunch, resolution, launcherMode);
        }
    } catch {
        // Not a valid wrapper URL; fall through to CLG / passthrough handling.
    }
    if (!urlIsGame(url)) return url;
    try {
        const parsedUrl = new URL(url);
        const embedded = parsedUrl.searchParams.get('embedded') ?? launcherMode;
        const gameId = parsedUrl.searchParams.get('gameId');
        if (!gameId) return url;
        const launcherBase = buildLauncherBaseUrl(resolution, !!embedded);
        return `${launcherBase}/${gameId}`;
    } catch {
        ddRumError({
            name: 'Invalid CLG URL',
            description: 'Invalid CLG URL',
            context: {
                url,
                error: new Error('Invalid CLG URL')
            },
            use_case: 'GLOBAL'
        });
        return url;
    }
};

export const addRegionToLauncherUrl = (url: string, region?: string): string => {
    if (!region) return url;
    try {
        const parsedUrl = new URL(url);
        parsedUrl.searchParams.set('region', region);
        return parsedUrl.toString();
    } catch {
        const separator = url.includes('?') ? '&' : '?';
        return url.includes('region=')
            ? url.replace(/([?&])region=[^&]*/, `$1region=${encodeURIComponent(region)}`)
            : `${url}${separator}region=${encodeURIComponent(region)}`;
    }
};
